import { useMemo, useState } from 'react';
import { useReactTable, getCoreRowModel, ColumnDef, flexRender } from '@tanstack/react-table';
import { trpc } from '@/lib/trpc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CustomButton } from '@/components/uiWrappers/button';
import { GoUpload } from 'react-icons/go';
import { AnalyticsCard } from './AnalyticsCard';

type VideoData = {
  videoId: string;
  videoName?: string;
  moduleNumber?: number;
  bucketNumber?: number;
  totalViews: number;
  uniqueViews: number;
  averageViewsPerUser: number;
};

function getCommonPinningStyles(column: any, isPinned: boolean): React.CSSProperties {
  return {
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned ? 0 : undefined,
    width: column.getSize(),
    zIndex: isPinned ? 10 : 1,
    boxShadow: isPinned ? '2px 0 5px -2px rgba(0,0,0,0.1)' : undefined,
  };
}

export function VideoAnalytics() {
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading } = trpc.admin.analytics.getVideoAnalytics.useQuery();
  const trpcUtils = trpc.useUtils();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const csvData = await trpcUtils.admin.analytics.exportVideoAnalyticsCSV.fetch();

      if (csvData) {
        // Create timestamp for filename
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `video_analytics_${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const videos = useMemo(() => data?.videos || [], [data?.videos]);

  const columns = useMemo<ColumnDef<VideoData>[]>(
    () => [
      {
        id: 'videoName',
        accessorKey: 'videoName',
        header: 'Video Name',
        size: 300,
        cell: (info) => <div className="font-medium">{info.getValue() as string || 'Unnamed Video'}</div>,
      },
      {
        id: 'module',
        accessorKey: 'moduleNumber',
        header: 'Module',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'bucket',
        accessorKey: 'bucketNumber',
        header: 'Bucket',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'totalViews',
        accessorKey: 'totalViews',
        header: 'Total Views',
        cell: (info) => <span className="text-brand">{info.getValue() as number}</span>,
      },
      {
        id: 'uniqueViews',
        accessorKey: 'uniqueViews',
        header: 'Unique Viewers',
        cell: (info) => <span className="text-brand">{info.getValue() as number}</span>,
      },
      {
        id: 'avgViews',
        accessorKey: 'averageViewsPerUser',
        header: 'Avg Views/User',
        cell: (info) => (info.getValue() as number).toFixed(1),
      },
    ],
    []
  );

  const table = useReactTable({
    data: videos,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnPinning: { left: ['videoName'] } },
    enableColumnPinning: true,
  });

  const totalVideos = videos.length;
  const totalViews = videos.reduce((sum, v) => sum + v.totalViews, 0);
  const uniqueViewers = videos.reduce((sum, v) => sum + v.uniqueViews, 0);
  const avgViewsPerVideo = totalVideos > 0 ? (totalViews / totalVideos).toFixed(1) : '0';
  const avgViewsPerUser = uniqueViewers > 0 ? (totalViews / uniqueViewers).toFixed(1) : '0';

  // Find most watched video
  const mostWatchedVideo = videos.length > 0
    ? videos.reduce((max, v) => v.totalViews > max.totalViews ? v : max, videos[0])
    : null;

  return (
    <div className="flex-1 min-w-0 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnalyticsCard
          label="Total Videos"
          value={totalVideos}
          tooltip="Total number of videos in the system (bucket-specific quiz result videos)"
        />
        <AnalyticsCard
          label="Total Views"
          value={totalViews}
          tooltip="Sum of all video views across all users"
        />
        <AnalyticsCard
          label="Unique Viewers"
          value={uniqueViewers}
          tooltip="Number of unique users who have viewed at least one video"
        />
        <AnalyticsCard
          label="Avg Views per Video"
          value={avgViewsPerVideo}
          tooltip="Average number of times each video has been viewed"
        />
        <AnalyticsCard
          label="Avg Views per User"
          value={avgViewsPerUser}
          tooltip="Average number of videos watched per user"
        />
        <AnalyticsCard
          label="Most Watched"
          value={mostWatchedVideo ? `${mostWatchedVideo.videoName} (${mostWatchedVideo.totalViews})` : 'N/A'}
          tooltip="The video with the highest number of total views"
          valueClassName="text-brand-dark text-base"
        />
      </div>

      <div className="bg-white min-w-0">
        <div className="pr-4 pb-4 border-b border-border flex justify-between items-center">
          <h2 className="text-xl font-bold text-foreground">Video Performance</h2>
          <CustomButton type="button" onClick={handleExport} disabled={isExporting} variant="outline" size="lg" className="rounded-lg flex items-center gap-2">
            <GoUpload className="size-5 text-primary" data-icon="inline-start" />
            Export
          </CustomButton>
        </div>

        <div className="flex flex-col justify-between">
          <div className="overflow-x-auto max-w-full">
            {isLoading ? (
              <div className="p-12 text-center"><p className="text-gray-500">Loading analytics...</p></div>
            ) : videos.length === 0 ? (
              <div className="p-12 text-center"><p className="text-gray-500">No video data available</p></div>
            ) : (
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isPinned = header.column.getIsPinned() === 'left';
                        return (
                          <TableHead key={header.id} className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider" style={getCommonPinningStyles(header.column, isPinned)}>
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="group hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => {
                        const isPinned = cell.column.getIsPinned() === 'left';
                        const isNameColumn = cell.column.id === 'videoName';
                        return (
                          <TableCell
                            key={cell.id}
                            className={`whitespace-nowrap bg-white ${
                              isNameColumn
                                ? 'text-sm font-semibold text-gray-900'
                                : 'text-sm font-medium text-gray-700'
                            } group-hover:bg-gray-50`}
                            style={getCommonPinningStyles(cell.column, isPinned)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
